import React, { FunctionComponent, useRef, MutableRefObject } from "react"
import { useTranslation } from "react-i18next"
import { useQuery } from "@apollo/client"
import { Card, Grid, Skeleton, useTheme } from "@mui/material"
import { UserService } from "../../../../services/user-service"
import moment from "moment"
import {
  GET_AVERAGE_EMPTYINGS_PER_HOUR_FOR_DISTRICT_QUERY,
  AverageEmptyingsPerHourForDistrictResult,
  AverageEmptyingsPerHourForDistrictVariables,
} from "../../../../api/graphql/queries/average-emptyings-per-hour-for-district"
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceLine,
  Label,
  BarChart,
  Bar,
  Tooltip,
} from "recharts"
import { Text } from "../../../partials/wrapper/text"
import lodash from "lodash"
import { useSelectedRegion } from "../../../../hooks/use-selected-region"

interface IEmptyingsPerHourChartProps {}

export const EmptyingsPerHourChart: FunctionComponent<IEmptyingsPerHourChartProps> = (props) => {
  const { t } = useTranslation()
  const { variables, skip } = useSelectedRegion()
  const ref = useRef(null)
  const theme = useTheme()

  const { data, loading } = useQuery<
    AverageEmptyingsPerHourForDistrictResult,
    AverageEmptyingsPerHourForDistrictVariables
  >(GET_AVERAGE_EMPTYINGS_PER_HOUR_FOR_DISTRICT_QUERY, {
    variables: {
      id: String(variables.id),
      startDate: moment().startOf("month").subtract(12, "months"),
      endDate: moment().endOf("month").subtract(1, "month"),
    },
    skip,
  })

  const getData = () => {
    if (!data) {
      return []
    }

    return data.averageEmptyingsPerHourForDistrict.emptyingsPerHour.map((entry) => ({
      date: moment(entry.month, "YYYY-MM").locale(UserService.getLanguage()).format("MMM"),
      value: Math.round(entry.emptyings_per_hour * 10) / 10,
    }))
  }

  // defined width for chart in order to have similar bar sizes
  const chartWidth = 140 + getData().length * 55
  const cardWidth = (ref as MutableRefObject<any>).current?.clientWidth - parseFloat(theme.spacing(4))
  const useCardWidth = chartWidth > cardWidth || loading

  return (
    <Grid item ref={ref} xs={12} md={6}>
      <Card sx={{ p: 1 }} ref={ref}>
        <Text variant="h6" bold>
          {t("emptyings_per_hour_chart.emptyings_per_hour")}
        </Text>
        {loading ? (
          <Skeleton width="100%" height={300} style={{ transform: "scale(1)" }} />
        ) : (
          <ResponsiveContainer width={lodash.max([useCardWidth ? cardWidth : chartWidth, 300])} height={300}>
            <BarChart
              data={getData()}
              margin={{
                left: 70,
                top: 70,
                right: 70,
                bottom: 70,
              }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />"
              <XAxis dataKey="date" />
              <YAxis tickFormatter={(value) => Math.round(value).toString()}>
                <Label position="top" offset={16}>
                  {t("emptyings_per_hour_chart.average_emptyings_label")}
                </Label>
              </YAxis>
              <Bar dataKey="value" fill={theme.palette.primary.light} />
              <Tooltip
                formatter={(value) => [
                  `${Number(value).toLocaleString(UserService.getLanguage(), {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })} ${t("emptyings_per_hour_chart.emptyings")}`,
                ]}
                cursor={false}
              />
              <ReferenceLine
                y={data?.averageEmptyingsPerHourForDistrict.average}
                strokeWidth={2}
                label={
                  <Label position="top" offset={10}>
                    {t("emptyings_per_hour_chart.average_emptyings", {
                      emptyings: data?.averageEmptyingsPerHourForDistrict.average.toLocaleString(
                        UserService.getLanguage(),
                        {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        },
                      ),
                    })}
                  </Label>
                }
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </Grid>
  )
}
